import React, { useState } from "react";
import axios from "../../axios";
import dayjs from "dayjs";
import {
  Card, CardContent, Typography, Grid, TextField,
  Button, Stack, Select, FormControl, InputLabel, MenuItem,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper,
  LinearProgress, Box
} from "@mui/material";
import { toast } from "react-toastify";

export default function VatReportPage() {
  const [filters, setFilters] = useState({
    companyId: localStorage.getItem("companyId") || "",
    branchId: "",
    fromDate: dayjs().startOf("month").format("YYYY-MM-DD"),
    toDate: dayjs().endOf("month").format("YYYY-MM-DD"),
    quarter: ""
  });

  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);

  const fmt = (n) => Number(n ?? 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const validateInputs = () => {
    if (!filters.companyId) {
      toast.error("❌ يجب اختيار الشركة");
      return false;
    }
    if (dayjs(filters.fromDate).isAfter(dayjs(filters.toDate))) {
      toast.error("❌ تاريخ البداية يجب أن يكون قبل تاريخ النهاية");
      return false;
    }
    return true;
  };

  const handleChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
  };

  const setMonthly = () => {
    setFilters(prev => ({
      ...prev,
      quarter: "",
      fromDate: dayjs().startOf("month").format("YYYY-MM-DD"),
      toDate: dayjs().endOf("month").format("YYYY-MM-DD")
    }));
  };

  const handleQuarterChange = (value) => {
    let start, end;
    const year = dayjs().year();

    switch (value) {
      case "Q1":
        start = dayjs(`${year}-01-01`);
        end = dayjs(`${year}-03-31`);
        break;
      case "Q2":
        start = dayjs(`${year}-04-01`);
        end = dayjs(`${year}-06-30`);
        break;
      case "Q3":
        start = dayjs(`${year}-07-01`);
        end = dayjs(`${year}-09-30`);
        break;
      case "Q4":
        start = dayjs(`${year}-10-01`);
        end = dayjs(`${year}-12-31`);
        break;
      default:
        return;
    }

    setFilters(prev => ({
      ...prev,
      quarter: value,
      fromDate: start.format("YYYY-MM-DD"),
      toDate: end.format("YYYY-MM-DD")
    }));
  };

  const setLastMonth = () => {
    const last = dayjs().subtract(1, "month");
    setFilters(prev => ({
      ...prev,
      quarter: "",
      fromDate: last.startOf("month").format("YYYY-MM-DD"),
      toDate: last.endOf("month").format("YYYY-MM-DD")
    }));
  };

  const setThisQuarter = () => {
    const q = Math.floor((dayjs().month()) / 3) + 1;
    handleQuarterChange(`Q${q}`);
  };

  const setLastQuarter = () => {
    const m = dayjs().month();
    const q = Math.floor(m / 3);
    const year = m < 3 ? dayjs().year() - 1 : dayjs().year();
    const map = {
      0: { s: `${year}-10-01`, e: `${year}-12-31` },
      1: { s: `${year}-01-01`, e: `${year}-03-31` },
      2: { s: `${year}-04-01`, e: `${year}-06-30` },
      3: { s: `${year}-07-01`, e: `${year}-09-30` }
    };
    const sel = map[q];
    setFilters(prev => ({
      ...prev,
      quarter: `Q${q === 0 ? 4 : q}`,
      fromDate: dayjs(sel.s).format("YYYY-MM-DD"),
      toDate: dayjs(sel.e).format("YYYY-MM-DD")
    }));
  };

  const setThisYear = () => {
    const year = dayjs().year();
    setFilters(prev => ({
      ...prev,
      quarter: "",
      fromDate: dayjs(`${year}-01-01`).format("YYYY-MM-DD"),
      toDate: dayjs(`${year}-12-31`).format("YYYY-MM-DD")
    }));
  };

  const setLastYear = () => {
    const year = dayjs().year() - 1;
    setFilters(prev => ({
      ...prev,
      quarter: "",
      fromDate: dayjs(`${year}-01-01`).format("YYYY-MM-DD"),
      toDate: dayjs(`${year}-12-31`).format("YYYY-MM-DD")
    }));
  };

  const resetFilters = () => {
    setFilters({
      companyId: localStorage.getItem("companyId") || "",
      branchId: "",
      fromDate: dayjs().startOf("month").format("YYYY-MM-DD"),
      toDate: dayjs().endOf("month").format("YYYY-MM-DD"),
      quarter: ""
    });
  };

  const fetchReport = async () => {
    if (!validateInputs()) return;

    try {
      setLoading(true);
      const res = await axios.get("/VatTypes/vat-report", {
        params: {
          companyId: filters.companyId,
          branchId: filters.branchId || null,
          fromDate: filters.fromDate,
          toDate: filters.toDate
        }
      });
      setReport(res.data);
    } catch (err) {
      console.error(err);
      toast.error("فشل في جلب الإقرار الضريبي");
    } finally {
      setLoading(false);
    }
  };

  const exportCsv = () => {
    if (!report) { toast.info("لا يوجد بيانات للتصدير"); return; }
    const rows = [
      ["البند", "القيمة", "الضريبة"],
      ["إجمالي المبيعات", report?.sales?.taxableSales ?? 0, report?.sales?.vatCollected ?? 0],
      ["إجمالي مرتجع المبيعات", report?.sales?.salesReturns ?? 0, report?.sales?.vatSalesReturn ?? 0],
      ["صافي ضريبة المبيعات", report?.sales?.netSales ?? 0, report?.sales?.netVATCollected ?? 0],
      ["إجمالي المشتريات", report?.purchases?.taxablePurchases ?? 0, report?.purchases?.vatPaid ?? 0],
      ["إجمالي مرتجع المشتريات", report?.purchases?.purchaseReturns ?? 0, report?.purchases?.vatPurchaseReturn ?? 0],
      ["صافي ضريبة المشتريات", report?.purchases?.netPurchases ?? 0, report?.purchases?.netVATPaid ?? 0],
      ["الضريبة المستحقة", (report?.sales?.netSales ?? 0) - (report?.purchases?.netPurchases ?? 0), report?.vatSummary?.vatDue ?? 0]
    ];
    const csv = rows.map(r => r.join(",")).join("\n");
    const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `vat-report-${filters.fromDate}_to_${filters.toDate}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const printReport = () => {
    window.print();
  };

  return (
    <Card>
      <CardContent>
        <Typography variant="h5" fontWeight="bold" mb={3}>
          🧾 الإقرار الضريبي
        </Typography>

        {loading && (
          <Box sx={{ mb: 2 }}><LinearProgress /></Box>
        )}

        {/* الفلاتر */}
        <Grid container spacing={2} mb={2}>
          <Grid item xs={12} sm={3}>
            <TextField
              type="date"
              label="من تاريخ"
              value={filters.fromDate}
              onChange={(e) => handleChange("fromDate", e.target.value)}
              fullWidth
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <TextField
              type="date"
              label="إلى تاريخ"
              value={filters.toDate}
              onChange={(e) => handleChange("toDate", e.target.value)}
              fullWidth
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
        </Grid>

        {/* أزرار */}
        <Stack direction="row" spacing={2} mb={3}>
          <Button variant="outlined" onClick={setMonthly}>📅 هذا الشهر</Button>
          <Button variant="outlined" onClick={setLastMonth}>📅 الشهر الماضي</Button>

          <FormControl sx={{ minWidth: 160 }}>
            <InputLabel>📊 ربع سنوي</InputLabel>
            <Select
              value={filters.quarter}
              label="📊 ربع سنوي"
              onChange={(e) => handleQuarterChange(e.target.value)}
            >
              <MenuItem value="Q1">الربع الأول (يناير - مارس)</MenuItem>
              <MenuItem value="Q2">الربع الثاني (أبريل - يونيو)</MenuItem>
              <MenuItem value="Q3">الربع الثالث (يوليو - سبتمبر)</MenuItem>
              <MenuItem value="Q4">الربع الرابع (أكتوبر - ديسمبر)</MenuItem>
            </Select>
          </FormControl>

          <Button variant="outlined" onClick={setThisQuarter}>📊 هذا الربع</Button>
          <Button variant="outlined" onClick={setLastQuarter}>📊 الربع الماضي</Button>
          <Button variant="outlined" onClick={setThisYear}>📆 هذا العام</Button>
          <Button variant="outlined" onClick={setLastYear}>📆 العام الماضي</Button>

          <Button variant="contained" onClick={fetchReport} disabled={loading}>
            {loading ? "جاري التحميل..." : "عرض التقرير"}
          </Button>
          <Button variant="outlined" color="secondary" onClick={resetFilters} disabled={loading}>
            إعادة التعيين
          </Button>
          <Button variant="outlined" onClick={exportCsv} disabled={loading || !report}>
            تنزيل CSV
          </Button>
          <Button variant="outlined" onClick={printReport} disabled={loading || !report}>
            طباعة
          </Button>
        </Stack>

        {/* ملخص سريع */}
        {report && (
          <Grid container spacing={2} mb={2}>
            <Grid item xs={12} sm={4}>
              <Paper sx={{ p: 2 }}>
                <Typography variant="subtitle2" color="text.secondary">صافي المبيعات</Typography>
                <Typography variant="h6">{fmt(report?.sales?.netSales)}</Typography>
                <Typography variant="body2">ضريبة المبيعات: {fmt(report?.sales?.netVATCollected)}</Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Paper sx={{ p: 2 }}>
                <Typography variant="subtitle2" color="text.secondary">صافي المشتريات</Typography>
                <Typography variant="h6">{fmt(report?.purchases?.netPurchases)}</Typography>
                <Typography variant="body2">ضريبة المشتريات: {fmt(report?.purchases?.netVATPaid)}</Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Paper sx={{ p: 2, bgcolor: "#f5f5f5" }}>
                <Typography variant="subtitle2" color="text.secondary">💰 الضريبة المستحقة</Typography>
                <Typography variant="h6">{fmt(report?.vatSummary?.vatDue)}</Typography>
                <Typography variant="body2">صافي: {fmt((report?.sales?.netSales ?? 0) - (report?.purchases?.netPurchases ?? 0))}</Typography>
              </Paper>
            </Grid>
          </Grid>
        )}

        {/* النتيجة */}
        {report ? (
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: "#1976d2" }}>
                  <TableCell sx={{ color: "#fff", fontWeight: "bold" }}>البند</TableCell>
                  <TableCell sx={{ color: "#fff", fontWeight: "bold" }} align="right">القيمة</TableCell>
                  <TableCell sx={{ color: "#fff", fontWeight: "bold" }} align="right">الضريبة</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                <TableRow>
                  <TableCell>إجمالي المبيعات</TableCell>
                  <TableCell align="right">{fmt(report?.sales?.taxableSales)}</TableCell>
                  <TableCell align="right">{fmt(report?.sales?.vatCollected)}</TableCell>
                </TableRow>           
                <TableRow>
                  <TableCell>إجمالي مرتجع المبيعات</TableCell>
                  <TableCell align="right">{fmt(report?.sales?.salesReturns)}</TableCell>
                  <TableCell align="right">{fmt(report?.sales?.vatSalesReturn)}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>صافي ضريبة المبيعات</TableCell>
                  <TableCell align="right">{fmt(report?.sales?.netSales)}</TableCell>
                  <TableCell align="right">{fmt(report?.sales?.netVATCollected)}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>إجمالي المشتريات</TableCell>
                  <TableCell align="right">{fmt(report?.purchases?.taxablePurchases)}</TableCell>
                  <TableCell align="right">{fmt(report?.purchases?.vatPaid)}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>إجمالي مرتجع المشتريات</TableCell>
                  <TableCell align="right">{fmt(report?.purchases?.purchaseReturns)}</TableCell>
                  <TableCell align="right">{fmt(report?.purchases?.vatPurchaseReturn)}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>صافي ضريبة المشتريات</TableCell>
                  <TableCell align="right">{fmt(report?.purchases?.netPurchases)}</TableCell>
                  <TableCell align="right">{fmt(report?.purchases?.netVATPaid)}</TableCell>
                </TableRow>
                <TableRow sx={{ backgroundColor: "#f5f5f5", fontWeight: "bold" }}>
                  <TableCell>💰 الضريبة المستحقة</TableCell>
                 <TableCell align="right">{fmt((report?.sales?.netSales ?? 0) - (report?.purchases?.netPurchases ?? 0))}</TableCell>
                  <TableCell align="right">{fmt(report?.vatSummary?.vatDue)}</TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        ) : (
          <Typography 
            variant="h6" 
            align="center" 
            sx={{ mt: 4, color: "text.secondary" }}
          >
            🔔 لا يوجد بيانات للفترة المحددة
          </Typography>
        )}

      </CardContent>
    </Card>
  );
}
